const DAY_MS = 86400000
const DEFAULT_OCCURRENCE_COUNT = 10
const MAX_OCCURRENCE_COUNT = 999

const SHIFTED_DIGITS = {
  ')': '0',
  '!': '1',
  '@': '2',
  '#': '3',
  $: '4',
  '%': '5',
  '^': '6',
  '&': '7',
  '*': '8',
  '(': '9'
}

export const DEFAULT_RECURRENCE_DRAFT = Object.freeze({
  recurrenceEndType: 'after',
  recurrenceEndCount: DEFAULT_OCCURRENCE_COUNT,
  recurrenceEndDate: ''
})

const clampOccurrenceCount = (value, fallback = 1) =>
  Math.max(1, Math.min(MAX_OCCURRENCE_COUNT, Math.floor(Number(value) || fallback)))

export const normalizeOccurrenceCount = (value) => {
  const digits = String(value ?? '')
    .split('')
    .map((character) => SHIFTED_DIGITS[character] || character)
    .join('')
    .replace(/\D/g, '')

  return digits ? clampOccurrenceCount(digits) : ''
}

export const recurrencePayloadFromBooking = (booking) => {
  const recurring = booking?.recurring || 'none'
  if (recurring === 'none') {
    return {
      recurring,
      recurringDays: [],
      recurrenceEndType: null,
      recurrenceEndCount: null,
      recurrenceEndDate: ''
    }
  }

  const recurrenceEndType = booking?.recurrenceEndType === 'on' ? 'on' : 'after'
  return {
    recurring,
    recurringDays: recurring === 'weekly' ? booking?.recurringDays || [] : [],
    recurrenceEndType,
    recurrenceEndCount:
      recurrenceEndType === 'after'
        ? clampOccurrenceCount(booking?.recurrenceEndCount, DEFAULT_OCCURRENCE_COUNT)
        : null,
    recurrenceEndDate:
      recurrenceEndType === 'on' ? booking?.recurrenceEndDate || booking?.date || '' : ''
  }
}

export const recurrenceFieldsFromMetadata = (metadata) => {
  const recurrence =
    metadata?.recurrence && typeof metadata.recurrence === 'object' ? metadata.recurrence : null

  return {
    recurring: recurrence?.frequency || 'none',
    recurringDays: Array.isArray(recurrence?.days) ? recurrence.days : [],
    recurrenceExcludedDates: Array.isArray(recurrence?.excludedDates)
      ? recurrence.excludedDates
      : [],
    recurrenceEndType: recurrence?.end?.type || null,
    recurrenceEndCount: recurrence?.end?.count ?? null,
    recurrenceEndDate: recurrence?.end?.date || ''
  }
}

export const recurrenceMetadataForEvent = (event) => {
  if (event?.recurring === undefined && event?.integrationMetadata === undefined) {
    return undefined
  }

  const metadata = { ...(event?.integrationMetadata || {}) }
  const rule = recurrencePayloadFromBooking(event)
  if (rule.recurring === 'none') {
    delete metadata.recurrence
    return metadata
  }

  // metadata.recurrence is REBUILT below, so an exclusion list persisted by
  // an earlier "delete only this event" must be carried forward explicitly.
  // A payload that OMITS recurrenceExcludedDates (an edit from a surface
  // without the events window) means "unchanged", never "cleared" — only an
  // explicit array (even an empty one) overrides the stored list.
  const prevExcluded = Array.isArray(metadata.recurrence?.excludedDates)
    ? metadata.recurrence.excludedDates
    : []
  const excluded =
    event.recurrenceExcludedDates === undefined ? prevExcluded : event.recurrenceExcludedDates

  metadata.recurrence = {
    frequency: rule.recurring,
    days: rule.recurringDays,
    ...(excluded?.length ? { excludedDates: [...new Set(excluded)].sort() } : {}),
    end:
      rule.recurrenceEndType === 'on'
        ? { type: 'on', date: rule.recurrenceEndDate }
        : { type: 'after', count: rule.recurrenceEndCount }
  }
  return metadata
}

const matchesRecurrence = (event, original, target) => {
  const targetDay = target.getDay()
  switch (event.recurring) {
    case 'daily':
      return true
    case 'weekdays':
      return targetDay >= 1 && targetDay <= 5
    case 'weekends':
      return targetDay === 0 || targetDay === 6
    case 'weekly': {
      const days = event.recurringDays
      return Array.isArray(days) && days.length
        ? days.includes(targetDay)
        : original.getDay() === targetDay
    }
    case 'biweekly': {
      if (original.getDay() !== targetDay) return false
      return Math.round((target - original) / DAY_MS) % 14 === 0
    }
    case 'monthly':
      return original.getDate() === target.getDate()
    case 'yearly':
      return original.getMonth() === target.getMonth() && original.getDate() === target.getDate()
    default:
      return false
  }
}

const weeklyOccurrenceNumber = (original, target, activeDays) => {
  const difference = Math.round((target - original) / DAY_MS)
  const fullWeeks = Math.floor(difference / 7)
  const remainder = difference % 7
  let count = 1 + fullWeeks * activeDays.size
  for (let offset = 1; offset <= remainder; offset++) {
    if (activeDays.has((original.getDay() + offset) % 7)) count++
  }
  return count
}

const occurrenceNumber = (event, original, target) => {
  const difference = Math.round((target - original) / DAY_MS)
  if (event.recurring === 'daily') return difference + 1
  if (event.recurring === 'biweekly') return Math.floor(difference / 14) + 1
  if (event.recurring === 'weekly') {
    const configured = Array.isArray(event.recurringDays) ? event.recurringDays : []
    return weeklyOccurrenceNumber(
      original,
      target,
      new Set(configured.length ? configured : [original.getDay()])
    )
  }
  if (event.recurring === 'weekdays') {
    return weeklyOccurrenceNumber(original, target, new Set([1, 2, 3, 4, 5]))
  }
  if (event.recurring === 'weekends') {
    return weeklyOccurrenceNumber(original, target, new Set([0, 6]))
  }
  if (event.recurring === 'monthly') {
    let count = 1
    const cursor = new Date(original.getFullYear(), original.getMonth() + 1, 1)
    const targetMonth = target.getFullYear() * 12 + target.getMonth()
    while (cursor.getFullYear() * 12 + cursor.getMonth() <= targetMonth) {
      const candidate = new Date(cursor.getFullYear(), cursor.getMonth(), original.getDate())
      if (candidate.getMonth() === cursor.getMonth()) count++
      cursor.setMonth(cursor.getMonth() + 1)
    }
    return count
  }
  if (event.recurring === 'yearly') {
    let count = 1
    for (let year = original.getFullYear() + 1; year <= target.getFullYear(); year++) {
      const candidate = new Date(year, original.getMonth(), original.getDate())
      if (candidate.getMonth() === original.getMonth()) count++
    }
    return count
  }
  return 1
}

// The original date is occurrence 1. An explicit end date is inclusive.
export const recurrenceAllowsDate = (event, target) => {
  if (!event?.recurring || event.recurring === 'none' || !(target instanceof Date)) {
    return false
  }

  const [year, month, day] = String(event.date || '')
    .split('-')
    .map(Number)
  if (!year) return false
  const original = new Date(year, month - 1, day)
  if (target < original) return false
  const targetDate = [target.getFullYear(), target.getMonth() + 1, target.getDate()]
    .map((part, index) => (index ? String(part).padStart(2, '0') : String(part)))
    .join('-')
  if (event.recurrenceExcludedDates?.includes(targetDate)) return false
  if (target.getTime() === original.getTime()) return true

  if (event.recurrenceEndType === 'on' && event.recurrenceEndDate) {
    const [endYear, endMonth, endDay] = event.recurrenceEndDate.split('-').map(Number)
    const endDate = new Date(endYear, endMonth - 1, endDay)
    if (!endYear || target > endDate) return false
  }
  if (!matchesRecurrence(event, original, target)) return false
  if (event.recurrenceEndType !== 'after') return true

  return occurrenceNumber(event, original, target) <= clampOccurrenceCount(event.recurrenceEndCount)
}
